import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import storage from "../Assets/js/Global";
import ProtectedRoute from "./ProtectedRoute";
import { PublicRoute } from "./PublicRoute";
import Loader from "../Components/Loader";
// Entry screens stay eager so the very first paint has no lazy-load flash.
import Login from "../pages/auth/Login";
import LanguageSelector from "../pages/auth/LanguageSelector";

// Retry the dynamic import once on failure — covers both transient network
// blips and the post-deployment case where the old chunk hash is gone but a
// global reload hasn't fired yet (e.g. the import happens before the
// unhandledrejection listener in index.js gets a chance to run).
function lazyWithRetry(importFn) {
  return lazy(() =>
    importFn().catch((err) => {
      if (
        err?.name === "ChunkLoadError" ||
        err?.message?.includes("Loading chunk") ||
        err?.message?.includes("Loading CSS chunk")
      ) {
        return importFn();
      }
      throw err;
    }),
  );
}

const Password = lazyWithRetry(() => import("../pages/auth/Password"));
const ForgotPassword = lazyWithRetry(
  () => import("../pages/auth/ForgotPassword"),
);
const VerifyOtp = lazyWithRetry(() => import("../pages/auth/VerifyOtp"));
const ResetPassword = lazyWithRetry(
  () => import("../pages/auth/ResetPassword"),
);

const Profile = lazyWithRetry(() => import("../pages/profile/Profile"));
const UserProfileEdit = lazyWithRetry(
  () => import("../pages/profile/UserProfileEdit"),
);
const HelpGuide = lazyWithRetry(() => import("../pages/HelpGuide"));
const ChangePassword = lazyWithRetry(() => import("../pages/ChangePassword"));
const DeleteAccount = lazyWithRetry(() => import("../pages/DeleteAccount"));
const StartScan = lazyWithRetry(() => import("../pages/scan/StartScan"));
const ScanGuidelines = lazyWithRetry(
  () => import("../pages/scan/ScanGuidelines"),
);
const Dashboard = lazyWithRetry(() => import("../pages/Dashboard"));
const ViewDetailsScreen = lazyWithRetry(
  () => import("../pages/scanDetails/ViewDetailsScreen"),
);
const Documents = lazyWithRetry(() => import("../pages/documents/Documents"));
const UploadDocument = lazyWithRetry(
  () => import("../pages/documents/UploadDocument"),
);
const History = lazyWithRetry(() => import("../pages/History"));
const ScanLogs = lazyWithRetry(() => import("../pages/ScanLogs"));
const PrivacyPolicy = lazyWithRetry(() => import("../pages/PrivacyPolicy"));
const Articles = lazyWithRetry(() => import("../pages/articles/Articles"));
const ArticlesDetails = lazyWithRetry(
  () => import("../pages/articles/ArticlesDetails"),
);
const UserScanDetails = lazyWithRetry(
  () => import("../pages/scanDetails/UserScanDetails"),
);
const TermsAndConditions = lazyWithRetry(
  () => import("../pages/TermsAndConditions"),
);
const AboutUs = lazyWithRetry(() => import("../pages/AboutUs"));
const WellnessInterests = lazyWithRetry(
  () => import("../pages/auth/SignUp/WellnessInterests"),
);
const ExportScanApprove = lazyWithRetry(
  () => import("../pages/ExportScanApprove"),
);
const ExportDocument = lazyWithRetry(() => import("../pages/ExportDocument"));
const ViewExportList = lazyWithRetry(() => import("../pages/ViewExportList"));
const Notification = lazyWithRetry(() => import("../pages/Notification"));
const DeleteAccountV2 = lazyWithRetry(
  () => import("../pages/DeleteAccount-v2"),
);
const GuestScan = lazyWithRetry(() => import("../pages/scan/GuestScan"));
const GuestHistory = lazyWithRetry(() => import("../pages/GuestHistory"));
const GuestList = lazyWithRetry(() => import("../pages/GuestList"));
const ListCampaign = lazyWithRetry(
  () => import("../pages/campaign/ListCampaign"),
);
const DetailsCampaign = lazyWithRetry(
  () => import("../pages/campaign/DetailsCampaign"),
);
const CampaignScan = lazyWithRetry(
  () => import("../pages/campaign/CampaignScan"),
);
const CampaignLanguageSelect = lazyWithRetry(
  () => import("../pages/campaign/CampaignLanguageSelect"),
);
const CampaignFaceScan = lazyWithRetry(
  () => import("../pages/campaign/CampaignFaceScan"),
);
const CampaignResult = lazyWithRetry(
  () => import("../pages/campaign/CampaignResult"),
);
const CreateCampaign = lazyWithRetry(
  () => import("../pages/campaign/CreateCampaign"),
);
const AccountDetails = lazyWithRetry(
  () => import("../pages/auth/SignUp/AccountDetails"),
);
const PhysicalProfile = lazyWithRetry(
  () => import("../pages/auth/SignUp/PhysicalProfile"),
);
const LifestyleQuestions = lazyWithRetry(
  () => import("../pages/auth/SignUp/LifestyleQuestions"),
);
const WellnessPriorities = lazyWithRetry(
  () => import("../pages/auth/SignUp/WellnessPriorities"),
);
const QrScanner = lazyWithRetry(() => import("../pages/QrScanner"));
const ViewQuestions = lazyWithRetry(
  () => import("../pages/profile/ViewQuestions"),
);
const ProductDetails = lazyWithRetry(
  () => import("../pages/product/ProductDetails"),
);
const AskAI = lazyWithRetry(() => import("../pages/AskAI"));
const ChartsScreen = lazyWithRetry(() => import("../pages/ChartsScreen"));
const FourLifeLogin = lazyWithRetry(
  () => import("../pages/auth/FourLifeLogin"),
);
const Dashboard2 = lazyWithRetry(() => import("../pages/Dashboard2"));
const MobileScanPrompt = lazyWithRetry(
  () => import("../pages/scan/MobileScanPrompt"),
);
const ShowAllResult = lazyWithRetry(() => import("../pages/ShowAllResult"));
const ShowAllResult2 = lazyWithRetry(() => import("../pages/ShowAllResult2"));
const PlanExpired = lazyWithRetry(() => import("../pages/PlanExpired"));
const WelcomeBack = lazyWithRetry(() => import("../pages/auth/WelcomeBack"));
const FourLifeScanHistory = lazyWithRetry(
  () => import("../pages/scanDetails/FourLifeScanHistory"),
);
const WebFaceScan = lazyWithRetry(() => import("../pages/scan/WebFaceScan"));
const ScanResults = lazyWithRetry(() => import("../pages/scan/ScanResults"));
const PublicScan = lazyWithRetry(() => import("../pages/scan/PublicScan"));
const PublicWebFaceScan = lazyWithRetry(
  () => import("../pages/scan/PublicWebFaceScan"),
);
const PublicScanResults = lazyWithRetry(
  () => import("../pages/scan/PublicScanResults"),
);
const BPCalibration = lazyWithRetry(() => import("../pages/BPCalibration"));

const NotFoundRedirect = () => {
  const loginObj = storage.get("loginObj");
  const isAuthenticated = loginObj && loginObj.userKey;
  const is4Life = process.env.REACT_APP_COMPANY_ID === "0it02f";

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={is4Life ? "/dashboard2" : "/dashboard"} replace />;
};

const AppRouter = () => {
  return (
    // Suspense shows the Loader while a lazily-loaded screen's chunk is fetched.
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute>
              <LanguageSelector />
            </PublicRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        {/* <Route
          path="/login-v2"
          element={
            <PublicRoute>
              <FourLifeLogin />
            </PublicRoute>
          }
        /> */}
        <Route
          path="/password"
          element={
            <PublicRoute>
              <Password />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/verify-otp"
          element={
            <PublicRoute>
              <VerifyOtp />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/wellness-interests"
          element={
            <PublicRoute>
              <WellnessInterests />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute company="globalhealth">
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard2"
          element={
            <ProtectedRoute company="4life">
              <Dashboard2 />
            </ProtectedRoute>
          }
        />
        <Route
          path="/show-all-result"
          element={
            <ProtectedRoute>
              <ShowAllResult />
            </ProtectedRoute>
          }
        />
        <Route
          path="/show-all-result-2"
          element={
            <ProtectedRoute>
              <ShowAllResult2 />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile-update"
          element={
            <ProtectedRoute>
              <UserProfileEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/language-selection"
          element={
            <ProtectedRoute>
              <LanguageSelector />
            </ProtectedRoute>
          }
        />
        <Route
          path="/deleteAcc-one"
          element={
            <ProtectedRoute>
              <DeleteAccount />
            </ProtectedRoute>
          }
        />
        <Route
          path="/delete-account"
          element={
            <PublicRoute>
              <DeleteAccountV2 />
            </PublicRoute>
          }
        />
        <Route
          path="/help-guide"
          element={
            <ProtectedRoute>
              <HelpGuide />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />

        <Route
          path="/start-scan"
          element={
            <ProtectedRoute>
              <StartScan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scan-guidelines"
          element={
            <ProtectedRoute>
              <ScanGuidelines />
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-more-details"
          element={
            <ProtectedRoute>
              <ViewDetailsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute company="globalhealth">
              <Documents />
            </ProtectedRoute>
          }
        />

        <Route
          path="/upload-document"
          element={
            <ProtectedRoute>
              <UploadDocument />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute company="globalhealth">
              <History />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scan-logs"
          element={
            <ProtectedRoute company="4life">
              <ScanLogs />
            </ProtectedRoute>
          }
        />
        <Route />
        <Route
          path="/privacy-policy"
          element={
            <ProtectedRoute>
              <PrivacyPolicy />
            </ProtectedRoute>
          }
        />
        <Route />
        <Route
          path="/terms-condition"
          element={
            <ProtectedRoute>
              <TermsAndConditions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/articles"
          element={
            <ProtectedRoute company="globalhealth">
              <Articles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/articles/:id"
          element={
            <ProtectedRoute company="globalhealth">
              <ArticlesDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-scan-details"
          element={
            <ProtectedRoute>
              <UserScanDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-scan-details-four-life"
          element={
            <ProtectedRoute company="4life">
              <FourLifeScanHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/about-us"
          element={
            <ProtectedRoute>
              <AboutUs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/privacy-consent"
          element={
            <ProtectedRoute>
              <ExportScanApprove />
            </ProtectedRoute>
          }
        />

        <Route
          path="/export-document"
          element={
            <ProtectedRoute>
              <ExportDocument />
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-export"
          element={
            <ProtectedRoute>
              <ViewExportList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notification"
          element={
            <ProtectedRoute>
              <Notification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-scan"
          element={
            <ProtectedRoute>
              <GuestScan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-history"
          element={
            <ProtectedRoute>
              <GuestHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-list"
          element={
            <ProtectedRoute>
              <GuestList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invites-list"
          element={
            <ProtectedRoute>
              <ListCampaign />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invites-create"
          element={
            <ProtectedRoute>
              <CreateCampaign />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invites-details"
          element={
            <ProtectedRoute>
              <DetailsCampaign />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite-language/:campaignCode"
          element={<CampaignLanguageSelect />}
        />
        <Route path="/invite/:campaignCode" element={<CampaignScan />} />
        <Route
          path="/invite/:campaignCode/guidelines"
          element={<ScanGuidelines />}
        />
        <Route
          path="/invite/:campaignCode/face"
          element={<CampaignFaceScan />}
        />
        <Route
          path="/invite/:campaignCode/result"
          element={<CampaignResult />}
        />

        <Route
          path="/view-questions"
          element={
            <ProtectedRoute>
              <ViewQuestions />
            </ProtectedRoute>
          }
        />

        <Route
          path="/product-details"
          element={
            <ProtectedRoute>
              <ProductDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/account-details"
          element={
            <PublicRoute>
              <AccountDetails />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/physical-profile"
          element={
            <PublicRoute>
              <PhysicalProfile />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/lifestyle"
          element={
            <PublicRoute>
              <LifestyleQuestions />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/wellness-priorities"
          element={
            <PublicRoute>
              <WellnessPriorities />
            </PublicRoute>
          }
        />
        <Route
          path="/qr-scanner"
          element={
            <PublicRoute>
              <QrScanner />
            </PublicRoute>
          }
        />
        <Route
          path="/web-scan"
          element={
            <ProtectedRoute>
              <MobileScanPrompt />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile-scan-prompt"
          element={
            <ProtectedRoute>
              <MobileScanPrompt />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scan-qr-code"
          element={
            <ProtectedRoute>
              <MobileScanPrompt />
            </ProtectedRoute>
          }
        />
        <Route
          path="/web-face-scan"
          element={
            <ProtectedRoute>
              <WebFaceScan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scan-results"
          element={
            <ProtectedRoute>
              <ScanResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ask-ai"
          element={
            <ProtectedRoute company="globalhealth">
              <AskAI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/charts-screen"
          element={
            <ProtectedRoute>
              <ChartsScreen />
            </ProtectedRoute>
          }
        />
        <Route path="/welcome-back" element={<WelcomeBack />} />
        <Route
          path="/plan-expired"
          element={
            <ProtectedRoute>
              <PlanExpired />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bp-calibration"
          element={
            <ProtectedRoute>
              <BPCalibration />
            </ProtectedRoute>
          }
        />
        {/* Public standalone scan flow (no login required) */}
        <Route path="/scan/:scanCode" element={<PublicScan />} />
        <Route path="/scan/:scanCode/face" element={<PublicWebFaceScan />} />
        <Route path="/scan/:scanCode/result" element={<PublicScanResults />} />
        {/* Catch-all: unknown routes redirect by auth + company */}
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
